<template>
  <vx-card :title="budget.name || 'Budget Details'" class="mb-4">
    <vs-button class="ml-auto" color="#2d3748" type="flat" icon-pack="feather" icon="icon-plus"
      @click="openPopup(null)">New
      Budget Line</vs-button>
    <div v-if="loading" class="text-center my-4">Loading...</div>
    <div v-else>
      <div v-if="!budget.budgetlines || budget.budgetlines.length === 0" class="text-center text-red-500">
        No budget lines found.
      </div>
      <div v-else>
        <div class="overflow-x-auto" ref="tableContainer">
          <div v-for="(lines, group) in groupedBudgetLines" :key="'group-' + group">
            <!-- Titre du groupe -->
            <!-- Tableau -->
            <table class="table-auto w-full border-collapse border border-gray-200 mb-4">
              <thead class="bg-gray-800 text-white">
                <tr>
                  <th class="border border-gray-300 px-4 py-2 text-left capitalize">{{ group }}</th>
                  <th v-for="(month, index) in months" :key="'header-' + month"
                    :class="{ 'current-month': isCurrentMonth(index) }"
                    class="border border-gray-300 px-4 py-2 text-center">
                    {{ month }}
                  </th>
                  <th class="border border-gray-300 px-4 py-2 text-left">Total</th>
                </tr>
              </thead>
              <tbody>
                <!-- Ligne des totaux -->
                <tr class="bg-gray-100 font-semibold">
                  <td class="border border-gray-300 px-4 py-2 text-left text-green-700">TOTAL</td>
                  <td v-for="month in monthsKeys" :key="'total-' + group + '-' + month"
                    class="border border-gray-300 px-4 py-2 text-center text-green-700">

                    <!-- Totaux EUR -->
                    <div v-if="calculateGroupTotal(lines, month, 'eur').budget > 0">
                      {{ toFi(calculateGroupTotal(lines, month, 'eur').actuals) }}
                      <small> / {{ toFi(calculateGroupTotal(lines, month, 'eur').budget) }} EUR</small> <feather-icon
                        v-if="getComparisonIcon(
                          calculateGroupTotal(lines, month, 'eur').actuals,
                          calculateGroupTotal(lines, month, 'eur').budget
                        )" :icon="getComparisonIcon(
                        calculateGroupTotal(lines, month, 'eur').actuals,
                        calculateGroupTotal(lines, month, 'eur').budget
                      ).icon" :svgClasses="getComparisonIcon(
                        calculateGroupTotal(lines, month, 'eur').actuals,
                        calculateGroupTotal(lines, month, 'eur').budget
                      ).color + ' stroke-current h-4 w-4 ml-2'" />

                    </div>

                    <!-- Totaux VOL -->
                    <div v-if="calculateGroupTotal(lines, month, 'vol').budget > 0">
                      {{ toFi(calculateGroupTotal(lines, month, 'vol').actuals) }}
                      <small> / {{ toFi(calculateGroupTotal(lines, month, 'vol').budget) }} {{ lines[0].unit }}</small>
                      <feather-icon v-if="getComparisonIcon(
                        calculateGroupTotal(lines, month, 'vol').actuals,
                        calculateGroupTotal(lines, month, 'vol').budget
                      )" :icon="getComparisonIcon(
                        calculateGroupTotal(lines, month, 'vol').actuals,
                        calculateGroupTotal(lines, month, 'vol').budget
                      ).icon" :svgClasses="getComparisonIcon(
                        calculateGroupTotal(lines, month, 'vol').actuals,
                        calculateGroupTotal(lines, month, 'vol').budget
                      ).color + ' stroke-current h-4 w-4 ml-2'" />
                    </div>
                  </td>
                  <td class="border border-gray-300 px-4 py-2 text-center text-green-700">
                    <div>
                      {{ toFi(calculateAnnualTotal(lines, 'eur').actuals) }}
                      <small> / {{ toFi(calculateAnnualTotal(lines, 'eur').budget) }} EUR</small>
                    </div>
                    <div>
                      {{ toFi(calculateAnnualTotal(lines, 'vol').actuals) }}
                      <small> / {{ toFi(calculateAnnualTotal(lines, 'vol').budget) }} {{ lines[0].unit }}</small>
                    </div>
                  </td>
                </tr>

                <!-- Lignes individuelles -->
                <tr v-for="line in lines" :key="line.id" class="hover:bg-gray-50">
                  <td class="border border-gray-300 px-4 py-2 text-left font-medium" @click="openPopup(line)">{{
                    line.name }}

                  </td>
                  <td v-for="(month, index) in monthsKeys" :key="'line-' + line.id + '-' + month"
                    class="border border-gray-300 px-4 py-2 text-center">

                    <div class="font-semibold text-gray-800" v-if="line[`${month}_eur`] > 0">
                      {{ (line.actuals && line.actuals.eur && toFi(line.actuals.eur[index + 1])) || '-' }} <small> / {{
                        toFi(line[`${month}_eur`]) || '-' }}
                        <small>EUR</small></small>
                    </div>

                    <div class="text-gray-500" v-if="line[`${month}_vol`] > 0">
                      {{ (line.actuals && line.actuals.vol && toFi(line.actuals.vol[index + 1])) || '-' }}
                      <small> / {{ toFi(line[`${month}_vol`]) || '-' }}
                        <small>{{ line.unit }}</small></small>
                    </div>
                  </td>
                  <td class="border border-gray-300 px-4 py-2 text-center font-semibold">
                    <div class="font-semibold text-gray-800">
                      {{ toFi(calculateAnnualTotal([line], 'eur').actuals) }} <small> / {{
                        toFi(calculateAnnualTotal([line], 'eur').budget)
                      }} EUR</small>
                    </div>
                    <div class="text-gray-500">
                      {{ toFi(calculateAnnualTotal([line], 'vol').actuals) }} <small> / {{
                        toFi(calculateAnnualTotal([line], 'vol').budget)
                      }} {{ line.unit }}</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <!-- Popup fullscreen -->
    <vs-popup title="New budget line" fullscreen :active.sync="isPopupOpen">
      <template v-slot:activator="{ on, attrs }"></template>
      <v-card>

        <!-- Content -->
        <div class="popup-content grid grid-cols-12 gap-4">
          <!-- Informations générales -->
          <div class="col-span-12 md:col-span-6">
            <h4 style="
      font-size: 12px;
      font-weight: bold;
      margin-top: 20px;
      border-bottom: 2px solid #444;
      padding-bottom: 5px;
      color: #444;
    ">
              General Information
            </h4>
            <vs-input label="Name" v-model="formData.name" class="mt-2 w-full" placeholder="" />
            <vs-input label="Group" v-model="formData.group" class="mt-2 w-full" placeholder="" />
            <vs-input label="Year" v-model="formData.year" class="mt-2 w-full" type="number" placeholder="" />
            <vs-input label="Unit" v-model="formData.unit" class="mt-2 w-full" placeholder="pcs, m3, etc." />
          </div>

          <!-- Requêtes SQL -->
          <div class="col-span-12 md:col-span-6">
            <h4 style="
      font-size: 12px;
      font-weight: bold;
      margin-top: 20px;
      border-bottom: 2px solid #444;
      padding-bottom: 5px;
      color: #444;
    ">
              SQL for actuals
            </h4>
            <vs-textarea style="height: auto" rows="3" class="w-full mt-8" label="SQL EUR (Actuals)"
              v-model="formData.sqleur" />
            <vs-textarea style="height: auto" rows="3" class="w-full mt-6" label="SQL UNIT (Actuals)"
              v-model="formData.sqlron" />
          </div>

          <!-- Montants mensuels -->
          <div class="col-span-12" v-if="formData.unit">
            <h4 style="
    font-size: 12px;
    font-weight: bold;
    margin-top: 20px;
    border-bottom: 2px solid #444;
    padding-bottom: 5px;
    color: #444;
  ">
              Totals and Monthly Budget
            </h4>

            <!-- Totals -->
            <div style="margin-top: 10px; display: flex; gap: 20px;">
              <div style="flex: 1;">
                <vs-input label="Total Annual EUR" v-model="totalAnnualEUR" type="number" step="0.01" style="
          width: 100%;
          padding: 2px;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 14px;
        " />
              </div>
              <div style="flex: 1;">
                <vs-input :label="'Total Annual ' + (formData.unit || '')" v-model="totalAnnualVOL" type="number"
                  step="0.01" style="
          width: 100%;
          padding: 2px;
          border: 1px solid #ddd;
          border-radius: 5px;
          font-size: 14px;
        " />
              </div>
            </div>

            <!-- Buttons for Distribution -->
            <div style="margin-top: 10px; display: flex; gap: 20px;">
              <vs-button @click="distributeTotal('eur')" color="primary" class="w-full" style="font-size: 12px;">
                Distribute Total Annual EUR
              </vs-button>
              <vs-button @click="distributeTotal('vol')" color="primary" class="w-full" style="font-size: 12px;">
                Distribute Total Annual {{ this.formData.unit }}
              </vs-button>
            </div>

            <!-- Monthly Budget -->
            <table style="
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
    font-size: 12px;
  ">
              <thead>
                <tr>
                  <th style="
          text-align: left;
          padding: 10px;
          background-color: #f8f8f8;
          color: #333;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
        ">
                    MONTH
                  </th>
                  <th style="
          text-align: left;
          padding: 1px;
          background-color: #f8f8f8;
          color: #333;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
        ">
                    EUR
                  </th>
                  <th style="
          text-align: left;
          padding: 1px;
          background-color: #f8f8f8;
          color: #333;
          font-weight: bold;
          border-bottom: 2px solid #ddd;
        ">
                    {{ this.formData.unit }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="month in monthsKeys" :key="month"
                  style="border-bottom: 1px solid #eee; background-color: #fff;">
                  <td style="padding: 1px; text-transform: capitalize; color: #555;">
                    {{ month.toUpperCase() }}
                  </td>
                  <td style="padding: 0px;">
                    <vs-input v-model="formData[`${month}_eur`]" type="number" step="0.01" placeholder="0.00"
                      @input="updateAnnualTotal('eur')" style="
              width: 100%;
              padding: 0px;
              border: 1px solid #ddd;
              border-radius: 5px;
              font-size: 14px;
            " />
                  </td>
                  <td style="padding: 0px;">
                    <vs-input v-model="formData[`${month}_vol`]" type="number" step="0.01" placeholder="0.00"
                      @input="updateAnnualTotal('vol')" style="
              width: 100%;
              padding: 0px;
              border: 1px solid #ddd;
              border-radius: 5px;
              font-size: 14px;
            " />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer -->
        <div class="popup-footer flex justify-end gap-4 p-4">
          <vs-button @click="saveLine" color="success">Save</vs-button>
        </div>
      </v-card>
    </vs-popup>
  </vx-card>

</template>


<script>
import moment from "moment";
import axios from "@/axios.js";
export default {
  data() {
    return {
      totalAnnualEUR: 0,
      totalAnnualVOL: 0,
      currentLine: null,
      formData: {
        name: "",
        group: "",
        year: new Date().getFullYear(),
        sqleur: "",
        sqlron: "",
      },
      isPopupOpen: false,
      loading: false,
      budget: {}, // Données du budget
      months: [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ],
      monthsKeys: [
        "jan", "feb", "mar", "apr", "may", "jun",
        "jul", "aug", "sep", "oct", "nov", "dec"
      ]
    };
  },
  computed: {
    // Regroupe les lignes par "group"
    groupedBudgetLines() {
      return this.budget.budgetlines.reduce((groups, line) => {
        if (!groups[line.group]) {
          groups[line.group] = [];
        }
        groups[line.group].push(line);
        return groups;
      }, {});
    }
  },
  methods: {
    toFi(value) {
      if (!isNaN(parseFloat(value))) {
        return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(parseFloat(value));
      } else {
        return '-';
      }
    },
    updateAnnualTotal(type) {
  const total = this.monthsKeys.reduce((sum, month) => {
    const value = parseFloat(this.formData[`${month}_${type}`]) || 0;
    return sum + value;
  }, 0);

  if (type === "eur") {
    this.totalAnnualEUR = total.toFixed(0);
  } else if (type === "vol") {
    this.totalAnnualVOL = total.toFixed(0);
  }
},

    // Répartit la valeur totale entre tous les mois
    distributeTotal(type) {
  const total = type === "eur" ? this.totalAnnualEUR : this.totalAnnualVOL;
  const monthlyValue = (parseFloat(total) / 12).toFixed(0);

  this.monthsKeys.forEach((month) => {
    this.$set(this.formData, `${month}_${type}`, monthlyValue);
  });

  this.updateAnnualTotal(type); // Met à jour la somme annuelle
},
    openPopup(line) {
      /*this.totalAnnualEUR = 0
      this.totalAnnualVOL = 0*/
      this.currentLine = line;
      if (line) {
        this.formData = { ...line }; // Remplit les champs pour l'édition
        this.updateAnnualTotal("eur")
        this.updateAnnualTotal("vol")
      } else {
        this.updateAnnualTotal("eur")
        this.updateAnnualTotal("vol")
        this.formData = {
          name: "",
          group: "",
          year: new Date().getFullYear(),
          sqleur: "",
          sqlron: "",
        };
      }
      this.isPopupOpen = true;
    },
    saveLine() {
      const payload = {
        name: this.formData.name,
        group: this.formData.group,
        year: this.formData.year,
        unit: this.formData.unit,
        idbudget: this.$route.params.idbudget,
        sqleur: this.formData.sqleur || "",
        sqlron: this.formData.sqlron || "",
      };

      // Ajout des montants mensuels
      this.monthsKeys.forEach((month) => {
        payload[`${month}_eur`] = this.formData[`${month}_eur`] || 0;
        payload[`${month}_vol`] = this.formData[`${month}_vol`] || 0;
      });

      if (this.currentLine) {
        // Mise à jour d'une ligne existante
        axios
          .put(`/budgetline/${this.currentLine.id}`, payload)
          .then(() => {
            this.$vs.notify({
              title: "Succès",
              text: "Ligne de budget mise à jour avec succès.",
              color: "success",
              position: "top-right",
            });
            this.isPopupOpen = false;
            this.fetchbudget();
          })
          .catch((error) => {
            console.error("Erreur lors de la mise à jour de la ligne :", error);
            this.$vs.notify({
              title: "Erreur",
              text: "Une erreur est survenue lors de la mise à jour.",
              color: "danger",
              position: "top-right",
            });
          });
      } else {
        // Création d'une nouvelle ligne
        axios
          .post("/budgetline", payload)
          .then(() => {
            this.$vs.notify({
              title: "Succès",
              text: "Ligne de budget ajoutée avec succès.",
              color: "success",
              position: "top-right",
            });
            this.isPopupOpen = false;
            this.fetchbudget();
          })
          .catch((error) => {
            console.error("Erreur lors de l'ajout de la ligne :", error);
            this.$vs.notify({
              title: "Erreur",
              text: "Une erreur est survenue lors de l'ajout de la ligne.",
              color: "danger",
              position: "top-right",
            });
          });
      }
    },

    /*
    saveLine() {
      // Préparation des données sous forme JSON
      const payload = {
        name: this.formData.name,
        group: this.formData.group,
        year: this.formData.year,
        unit: this.formData.unit,
        budgetId: this.budgetId,
        sqleur: this.formData.sqleur || "",
        sqlron: this.formData.sqlron || "",
        idbudget: this.$route.params.idbudget || "",
      };

      // Ajout des montants mensuels
      this.monthsKeys.forEach((month) => {
        payload[`${month}_eur`] = this.formData[`${month}_eur`] || 0;
        payload[`${month}_vol`] = this.formData[`${month}_vol`] || 0;
      });

      // Requête POST avec Axios
      axios
        .post("/budgetline", payload)
        .then((response) => {
          this.$vs.notify({
            title: "Succès",
            text: "Ligne de budget ajoutée avec succès",
            color: "success",
            position: "top-right",
          });

          this.isPopupOpen = false;
          this.fetchbudget();
        })
        .catch((error) => {
          console.error("Erreur lors de l'ajout de la ligne de budget :", error);
          this.$vs.notify({
            title: "Erreur",
            text: "Une erreur est survenue lors de l'ajout de la ligne.",
            color: "danger",
            position: "top-right",
          });
        });
    },*/
    getComparisonIcon(actuals, budget) {
      const actualsNum = parseFloat(actuals) || 0; // Convertir en nombre ou 0 par défaut
      const budgetNum = parseFloat(budget) || 0;

      if (actualsNum >= budgetNum) {
        //alert(`${actualsNum} >= ${budgetNum} : TOP`);
        return {
          icon: "ActivityIcon",
          color: "text-top", // Couleur HTML pour vert
        };
      } else {
        //alert(`${actualsNum} < ${budgetNum} : BAD`);
        return {
          icon: "AlertTriangleIcon",
          color: "text-bad", // Couleur HTML pour rouge
        };
      }
    },
    scrollToCurrentMonth() {
      const currentMonthIndex = moment().month(); // Mois actuel (0 = janvier)
      const container = this.$refs.tableContainer;
      if (!container) return;

      // Ajouter un délai
      setTimeout(() => {
        // Trouver la première colonne pour calculer dynamiquement la largeur
        const firstColumn = container.querySelector("th");
        if (!firstColumn) return;

        const columnWidth = firstColumn.offsetWidth; // Largeur réelle d'une colonne
        const scrollPosition = currentMonthIndex * columnWidth;

        container.scrollTo({
          left: scrollPosition,
          behavior: "smooth", // Défilement fluide
        });
      }, 1000); // Délai de 300 ms
    },
    isCurrentMonth(index) {
      const currentMonth = moment().month(); // Mois actuel (0 = janvier)
      return index === currentMonth;
    },
    // Calculer le total pour un groupe et un type (EUR ou Volume)
    calculateAnnualTotal(lines, type) {
      const totals = lines.reduce(
        (acc, line) => {
          // Vérification pour les actuals
          let actualsForType = line.actuals && line.actuals[type];
          let actualsTotal = 0;
          if (actualsForType) {
            actualsTotal = Object.values(actualsForType).reduce(
              (sum, value) => sum + parseFloat(value || 0),
              0
            );
          }

          // Calcul des budgets
          const budgetTotal = this.monthsKeys.reduce(
            (sum, month) => sum + parseFloat(line[`${month}_${type}`] || 0),
            0
          );

          // Accumuler les totaux
          acc.actuals += actualsTotal;
          acc.budget += budgetTotal;

          return acc;
        },
        { actuals: 0, budget: 0 }
      );

      // Si les totaux sont 0, retourne '-'
      return {
        actuals: totals.actuals === 0 ? '-' : totals.actuals.toFixed(0),
        budget: totals.budget === 0 ? '-' : totals.budget.toFixed(0),
      };
    },
    calculateGroupTotal(lines, monthKey, type) {
      const monthIndex = this.monthsKeys.indexOf(monthKey) + 1; // Convertir "jan" en 1, "feb" en 2, etc.
      const totals = lines.reduce(
        (acc, line) => {
          // Vérification explicite des actuals
          const actualsForType = line.actuals && line.actuals[type];
          const actualValue =
            actualsForType && actualsForType[monthIndex]
              ? parseFloat(actualsForType[monthIndex]) || 0
              : 0;

          // Gestion des budgets
          const budgetValue = parseFloat(line[`${monthKey}_${type}`] || 0);

          // Ajouter aux totaux
          acc.actuals += actualValue;
          acc.budget += budgetValue;

          return acc;
        },
        { actuals: 0, budget: 0 } // Initialisation des totaux
      );

      return {
        actuals: totals.actuals === 0 ? '-' : totals.actuals.toFixed(0),
        budget: totals.budget === 0 ? '-' : totals.budget.toFixed(0),
      };
    },
    async fetchbudget() {
      this.loading = true;
      try {
        const response = await axios.get("budget/data/" + this.$route.params.idbudget);
        this.budget = response.data;
        this.$nextTick(() => {
          this.scrollToCurrentMonth(); // Appeler après le rendu
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des budgets:", error);
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    const roles = localStorage.getItem("login-roles");
    if (roles) {
      const rolesLower = roles.toLowerCase();
      if (rolesLower.includes("accountant")) {
        this.$router.push({ path: "/outlay/outlay-list" });
      }
      if (rolesLower.includes("admin")) {
        this.fetchbudget();
      } else {
        this.$vs.notify({
          title: "Denied",
          text: "Access to the Finance page is reserved for administrators",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
          position: "bottom-right",
          time: 8000,
        });
        this.$router.push({ path: "/dashboard/analytics" });
      }
    } else {
      this.$vs.notify({
        title: "Erreur",
        text: "Roluri de autentificare lipsă.",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger",
        position: "bottom-right",
        time: 8000,
      });
      this.$router.push({ path: "/login" });
    }
  }
};
</script>

<style scoped>
.current-month {
  color: #ddadad;
}

/* Conteneur principal */
.overflow-x-auto {
  overflow-x: auto;
  white-space: nowrap;
  /* Empêche les colonnes de se casser */
  border-radius: 8px;
  background-color: #ffffff;
  margin-top: 16px;
}

/* Titre des groupes */
.group-title {
  font-size: 18px;
  font-weight: bold;
  color: #9ac3b9;
  /* Vert clair */
  margin-top: 20px;
  margin-left: 20px;
  margin-bottom: 10px;
  text-transform: capitalize;
}

/* Tableau */
.table-auto {
  width: 100%;
  border-collapse: collapse;
  font-family: "Poppins", sans-serif;
  font-size: 15px;
  color: #333;
}

.table-auto th,
.table-auto td {
  width: 200px;
  /* Largeur fixe */
  max-width: 200px;
  /* Limite la largeur */
  min-width: 200px;
  /* Limite la largeur */
  text-align: center;
  /* Centre le texte */
  overflow: hidden;
  /* Empêche le débordement */
  text-overflow: ellipsis;
  /* Affiche "..." pour le contenu dépassant */
  white-space: nowrap;
  /* Empêche le retour à la ligne */
}

/* Première colonne figée */
.table-auto th:first-child,
.table-auto td:first-child {
  font-size: 14px;
  width: 200px;
  /* Largeur fixe */
  max-width: 200px;
  /* Limite la largeur */
  min-width: 200px;
  /* Limite la largeur */
  position: sticky;
  left: 0;
  z-index: 3;
  /* Assure que la colonne reste visible au-dessus des autres */
  background-color: #9ac3b9;
  /* Vert clair */
  color: #fff;
  /* Texte gris foncé */
  font-weight: bold;
  box-shadow: 2px 0px 5px rgba(0, 0, 0, 0.1);
  /* Ombre à droite */
}

/* Dernière colonne figée */
.table-auto th:last-child,
.table-auto td:last-child {
  position: sticky;
  right: 0;
  z-index: 3;
  /* Assure que la colonne reste visible au-dessus des autres */
  background-color: #ddadad;
  /* Vert clair */
  color: #fff;
  /* Texte gris foncé */
  font-weight: bold;
  box-shadow: -2px 0px 5px rgba(0, 0, 0, 0.1);
  /* Ombre à gauche */
}

/* Style personnalisé pour la première ligne (en-tête) de la première colonne */
.table-auto th:first-child {
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: #2d3748;
  /* Vert plus foncé */
  color: #fff;
  /* Texte gris foncé */
  font-weight: bold;
  padding: 12px;
}

/* Style personnalisé pour la première ligne (en-tête) de la dernière colonne */
.table-auto th:last-child {
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: #2d3748;
  /* Vert plus foncé */
  color: #fff;
  /* Texte gris foncé */
  font-weight: bold;
  padding: 12px;
}

/* En-tête */
thead th {
  background-color: #2d3748;
  /* Noir/gris foncé */
  color: #fff;
  text-align: center;
  font-weight: bold;
  border-bottom: 2px solid #4a5568;
}

/* Cellules */
tbody td {
  border: 0.5px solid #e2e8f0;
  color: #555;
}

/* Ligne des totaux */
.bg-gray-100 {
  background-color: #9ac3b9;
  /* Vert clair */
}

.text-green-700 {
  color: #edf2f7;
}

.font-medium {
  font-weight: 500;
}

/* Survol des lignes */
tbody tr:hover {
  cursor: pointer;
  /*border: 2px dashed #4a5568;*/
  /* Bordure grise en pointillés */
}

/* Effet sur survol des colonnes */
tbody td:hover {
  cursor: pointer;
  /*border: 2px dashed #4a5568;*/
  /* Bordure grise en pointillés */
}

/* Alignement et mise en évidence */
td.text-left {
  text-align: left;
}

td.text-center {
  text-align: center;
}
</style>

/*created() {
const roles = localStorage.getItem("login-roles");
if (roles) {
const rolesLower = roles.toLowerCase();
if (rolesLower.includes("accountant")) {
this.$router.push({ path: "/outlay/outlay-list" });
}
if (rolesLower.includes("admin")) {
this.fetchbudget();
} else {
this.$vs.notify({
title: "Denied",
text: "Access to the Finance page is reserved for administrators",
iconPack: "feather",
icon: "icon-alert-circle",
color: "danger",
position: "bottom-right",
time: 8000,
});
this.$router.push({ path: "/dashboard/analytics" });
}
} else {
this.$vs.notify({
title: "Erreur",
text: "Roluri de autentificare lipsă.",
iconPack: "feather",
icon: "icon-alert-circle",
color: "danger",
position: "bottom-right",
time: 8000,
});
this.$router.push({ path: "/login" });
}
},*/